import { Button } from "@msuite/ui/button";
import { useDisclosure } from "@msuite/ui/hooks";
import { Icon, icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { createStandardMenuItems, Menu, type MenuItems } from "@msuite/ui/menu";
import { ConfirmModal, useAsyncModal } from "@msuite/ui/modal";
import { invalidate, useQueryClient } from "@msuite/ui/query";
import { responseHandler } from "@msuite/ui/utils";
import type { RoleListItem } from "@msuite/utils/types";
import { Fragment } from "react";
import { hasRoleMenu } from "@/components/citadel";
import { client } from "@/hono";
import { DuplicateRoleModal } from "@/modals/organizations/duplicate-role-modal";
import { EditRoleModal } from "@/modals/organizations/edit-role-modal";

/** Props Interface */
interface RoleRowMenuProps {
	role: RoleListItem;
}

/**
 * The per-role actions in the list: rename, duplicate, delete. The system roles show none —
 * every entry would be refused — and the lock beside their names is what explains the absence.
 *
 * The whole roles tab is already gated on owner-or-admin, so this does not re-check it; it only
 * hides what the server would refuse for the role itself.
 */
export function RoleRowMenu({ role }: RoleRowMenuProps) {
	/** Context */
	const { t } = useTranslation();

	/** Hooks */
	const qc = useQueryClient();
	const asyncModal = useAsyncModal();
	const editRoleModalDisclosure = useDisclosure();
	const duplicateRoleModalDisclosure = useDisclosure();

	/** Functions */
	async function invalidateRoles() {
		// The write reaches three readers: the roles list, the caller's own abilities, and the
		// individual permission grid that names roles as sources.
		await qc.invalidateQueries({
			predicate: invalidate(["roles", "citadel", "user-permissions-form"]),
		});
	}

	async function handleOnDeleteRole() {
		const isConfirmed = await asyncModal.confirm();
		if (!isConfirmed) return;
		try {
			const response = await client.roles[":roleId"].$delete({ param: { roleId: role.id } });
			await responseHandler(response, false);
			await invalidateRoles();
		} catch (error) {
			// Surfaced rather than swallowed, so a refused delete says why instead of doing nothing.
			// The list is unchanged, so nothing needs undoing.
			console.error(error);
		}
	}

	/** Computed */
	const items: MenuItems = [
		{
			label: t("general.duplicate"),
			icon: icon("Copy"),
			onClick: duplicateRoleModalDisclosure.onOpen,
		},
	];

	/** Guard */
	// A system role cannot be renamed, deleted or duplicated — no menu at all.
	if (!hasRoleMenu(role)) return null;

	/** Render */
	return (
		<Fragment>
			<Menu
				items={createStandardMenuItems({
					onEdit: editRoleModalDisclosure.onOpen,
					// The entry says what the modal can do — a role's edit is its name, nothing else.
					onEditOptions: { label: t("general.rename") },
					onDelete: handleOnDeleteRole,
					items,
				})}
			>
				<Button
					size="xxs"
					variant="ghost"
					icon={<Icon name="EllipsisVertical" />}
					data-cy={`role-row-menu-${role.id}`}
				/>
			</Menu>
			{editRoleModalDisclosure.isOpen && (
				<EditRoleModal
					isOpen={editRoleModalDisclosure.isOpen}
					onClose={editRoleModalDisclosure.onClose}
					roleId={role.id}
					onComplete={invalidateRoles}
				/>
			)}
			{duplicateRoleModalDisclosure.isOpen && (
				<DuplicateRoleModal
					isOpen={duplicateRoleModalDisclosure.isOpen}
					onClose={duplicateRoleModalDisclosure.onClose}
					roleId={role.id}
					onComplete={invalidateRoles}
				/>
			)}
			{asyncModal.isOpen && (
				<ConfirmModal
					controller={asyncModal}
					title={t("roles.delete_role")}
					message={t("roles.delete_confirmation_message", {
						count: String(role.memberCount),
					})}
				/>
			)}
		</Fragment>
	);
}
